import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Search, AlertCircle, Link2, Plus, Trash2, Undo2, Wand2 } from 'lucide-react';
import { buildTargets, savedLinkMap, suggestionsFor, searchTargets, toSaveLink } from '../utils/qboCustomerLinks';
import accountsAgentService from '../services/AccountsAgentService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import './QuickBooksCustomersPage.css';

// Admin > Billing > QuickBooks Customers.
//
// THE CUSTOMERS ARE READ-ONLY: QuickBooks is where they are kept, and they are listed through the accounts
// agent's own QuickBooks connection (code-accounts, GET /finance/qbo/customers?list=all) with the same fields
// as the agent's Customer list report. Change a customer in QuickBooks.
//
// WHAT IS EDITED HERE is the link from each customer to the ONE hconnect partner, chain or hotel it pays for
// (acct_customer_links — exactly one target per customer, V566; choosing another replaces it). A partner, chain
// or hotel whose name matches the customer's is offered as a suggestion — styled apart from a link, so a list
// of guesses is never read as several links — and never
// applied on its own: "Rotana" the customer and "Rotana Hotels" the chain look alike, but so can two
// unrelated companies. Nothing is written until Save.

const PICKER_LIMIT = 15;

const money = (n, ccy) => (n == null ? '—'
    : new Intl.NumberFormat('en-IE', { style: 'currency', currency: ccy || 'EUR' }).format(Number(n)));

function QuickBooksCustomersPage() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [hub, setHub] = useState({ links: [], accounts: [], hotels: [] });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('Active');
    const [linkShow, setLinkShow] = useState('');
    const [pending, setPending] = useState(() => new Map());   // qboCustomerId -> 'account:12' | 'hotel:34' | ''
    const [picking, setPicking] = useState(null);
    const [pickerSearch, setPickerSearch] = useState('');
    const [confirmModal, setConfirmModal] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [customers, links] = await Promise.all([
                accountsAgentService.qboAllCustomers(),
                accountsAgentService.customerLinks(),
            ]);
            setRows(customers);
            setHub(links);
            setPending(new Map());
        } catch (e) {
            setError(e.response?.data?.message || e.response?.data?.error?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    // ── Hub lookups ─────────────────────────────────────────────────────────────────────────────────────
    const accountById = useMemo(() => new Map(hub.accounts.map(a => [a.accountId, a])), [hub]);
    const hotelById = useMemo(() => new Map(hub.hotels.map(h => [h.hotelId, h])), [hub]);
    const savedLink = useMemo(() => savedLinkMap(hub), [hub]);
    const targets = useMemo(() => buildTargets(hub), [hub]);
    const targetByKey = useMemo(() => new Map(targets.map(t => [t.key, t])), [targets]);

    const linkOf = useCallback((id) => (pending.has(id) ? pending.get(id) : (savedLink.get(id) || '')), [pending, savedLink]);
    const labelOf = useCallback((key) => {
        if (!key) return '';
        const t = targetByKey.get(key);
        if (t) return t.label;
        const [type, id] = key.split(':');
        if (type === 'partner') return `Partner ${id}`;
        return type === 'account' ? `Chain ${accountById.get(+id)?.accountName || id}` : `Hotel ${hotelById.get(+id)?.hotelName || id}`;
    }, [targetByKey, accountById, hotelById]);

    const suggestionsOf = useCallback((r) => suggestionsFor(r, targets), [targets]);

    const setLink = (id, key) => {
        setPending(prev => {
            const next = new Map(prev);
            if ((key || '') === (savedLink.get(id) || '')) next.delete(id); else next.set(id, key || '');
            return next;
        });
        setPicking(null);
        setPickerSearch('');
    };

    // DELETE A SAVED MAPPING — immediately, after a confirm, rather than as one more pending change: a removal
    // that waited for "Save links" looked like it had happened when it had not. Only this customer's row is
    // touched, so other unsaved changes on the page survive.
    const deleteLink = (r) => {
        const id = r.customerId;
        setConfirmModal({
            title: 'Delete mapping',
            message: `Delete the mapping of "${r.customer}" to ${labelOf(savedLink.get(id))}? The QuickBooks customer is not changed; it just stops being linked to anything in hconnect.`,
            confirmLabel: 'Delete',
            onConfirm: async () => {
                setBusy(true);
                setError(null);
                setNotice(null);
                try {
                    await accountsAgentService.saveCustomerLinks([toSaveLink(String(id), '')]);
                    setHub(prev => ({ ...prev, links: (prev.links || []).filter(l => String(l.qboCustomerId) !== String(id)) }));
                    setPending(prev => { const next = new Map(prev); next.delete(id); return next; });
                    setNotice(`Mapping for ${r.customer} deleted.`);
                } catch (e) {
                    setError(e.response?.data?.message || e.response?.data?.error?.message || e.message);
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    const pickerResults = useMemo(() => searchTargets(targets, pickerSearch, PICKER_LIMIT), [targets, pickerSearch]);

    const save = async () => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            const links = [...pending.entries()].map(([qboCustomerId, key]) => toSaveLink(qboCustomerId, key));
            const r = await accountsAgentService.saveCustomerLinks(links);
            await load();
            setNotice(`${r.linked} link(s) saved${r.cleared ? `, ${r.cleared} removed` : ''}.`);
        } catch (e) {
            setError(e.response?.data?.message || e.response?.data?.error?.message || e.message);
        } finally {
            setBusy(false);
        }
    };

    // ── Filtering ───────────────────────────────────────────────────────────────────────────────────────
    const visible = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return rows.filter(r => {
            if (status && r.customerStatus !== status) return false;
            const linked = !!linkOf(r.customerId);
            if (linkShow === 'linked' && !linked) return false;
            if (linkShow === 'unlinked' && linked) return false;
            if (!needle) return true;
            return [r.customer, r.company, r.contact, r.email, r.phone, r.address, r.vatNumber, labelOf(linkOf(r.customerId))]
                .some(v => String(v || '').toLowerCase().includes(needle));
        });
    }, [rows, search, status, linkShow, linkOf, labelOf]);

    const owing = visible.filter(r => Number(r.balance) > 0).length;
    const linkedCount = rows.filter(r => linkOf(r.customerId)).length;

    return (
        <div className="qbc-page">
            <div className="qbc-header">
                <div>
                    <h1>QuickBooks Customers</h1>
                    <p className="qbc-subtitle">
                        Customers in QuickBooks Online, read-only — change them in QuickBooks. Link each one to the
                        one hconnect partner, chain or hotel it pays for.
                    </p>
                </div>
                <div className="qbc-header-actions">
                    <Button variant="secondary" onClick={() => navigate('/admin/billing/quickbooks-customers/link')}>
                        <Wand2 className="icon" /> Link customers
                    </Button>
                    <Button variant="success" onClick={load} disabled={loading || busy}><RefreshCw className="icon" /> Refresh</Button>
                </div>
            </div>

            {error && <div className="qbc-error"><AlertCircle size={16} /> {error}</div>}
            {notice && <div className="qbc-notice">{notice}</div>}

            <div className="qbc-card">
                <div className="qbc-controls">
                    <label className="qbc-search">
                        <Search size={14} />
                        <input placeholder="Search name, contact, email, phone, address, VAT number or linked partner/chain/hotel" value={search} onChange={e => setSearch(e.target.value)} />
                    </label>
                    <select value={status} onChange={e => setStatus(e.target.value)}>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="">All statuses</option>
                    </select>
                    <select value={linkShow} onChange={e => setLinkShow(e.target.value)}>
                        <option value="">Linked and not linked</option>
                        <option value="unlinked">Not linked</option>
                        <option value="linked">Linked</option>
                    </select>
                    <span className="qbc-count">{visible.length} customer(s) · {owing} owing · {linkedCount} of {rows.length} linked</span>
                </div>

                {pending.size > 0 && (
                    <div className="qbc-savebar">
                        <span>{pending.size} unsaved link change(s)</span>
                        <Button variant="secondary" onClick={() => setPending(new Map())} disabled={busy}>Discard</Button>
                        <Button variant="success" onClick={save} disabled={busy}>Save links</Button>
                    </div>
                )}

                {loading ? (
                    <div className="qbc-loading"><Spinner /></div>
                ) : (
                    <div className="qbc-scroll">
                        <table className="qbc-table">
                            <thead>
                                <tr>
                                    <th>Customer</th><th>Billing email</th><th>Address</th><th>VAT number</th>
                                    <th className="qbc-num">Balance</th><th>Status</th><th>hconnect partner / chain / hotel</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(r => {
                                    const id = r.customerId;
                                    const link = linkOf(id);
                                    const sugg = link ? [] : suggestionsOf(r);
                                    return (
                                        <tr key={id} className={pending.has(id) ? 'qbc-row-changed' : ''}>
                                            <td>
                                                {r.customer}
                                                {r.company && r.company !== r.customer && <div className="qbc-muted">{r.company}</div>}
                                                {r.contact && <div className="qbc-muted">{r.contact}{r.phone ? ` · ${r.phone}` : ''}</div>}
                                            </td>
                                            <td>{r.email || '—'}</td>
                                            <td>{r.address || '—'}</td>
                                            <td>{r.vatNumber || '—'}</td>
                                            <td className="qbc-num">{money(r.balance, r.currency)}</td>
                                            <td><span className={`qbc-chip ${r.customerStatus === 'Active' ? 'qbc-chip-active' : ''}`}>{r.customerStatus}</span></td>
                                            <td className="qbc-link-cell">
                                                {link && (
                                                    <div className="qbc-linked-row">
                                                        <div className="qbc-linked" title="Linked — this customer pays for this one partner, chain or hotel">
                                                            <Link2 size={13} />
                                                            <span>{labelOf(link)}{pending.has(id) ? ' (unsaved)' : ''}</span>
                                                        </div>
                                                        {pending.has(id) ? (
                                                            <button type="button" className="qbc-link-btn" title="Undo this unsaved change" onClick={() => setLink(id, savedLink.get(id) || '')}>
                                                                <Undo2 size={13} /> Undo
                                                            </button>
                                                        ) : (
                                                            <button type="button" className="qbc-delete-btn" title="Delete this mapping" onClick={() => deleteLink(r)} disabled={busy}>
                                                                <Trash2 size={13} /> Delete
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                {/* GUESSES, NOT LINKS. Several may be offered; picking one links the
                                                    customer to that one alone. */}
                                                {sugg.length > 0 && (
                                                    <div className="qbc-suggestions">
                                                        <span className="qbc-suggest-heading">Not linked — suggested:</span>
                                                        {sugg.map(t => (
                                                            <button key={t.key} type="button" className="qbc-suggest" title="Link this customer to it" onClick={() => setLink(id, t.key)}>
                                                                <Plus size={12} /> {t.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                                {picking === id ? (
                                                    <div className="qbc-picker">
                                                        <input autoFocus placeholder="Search partners, chains and hotels" value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} />
                                                        {pickerResults.map(t => (
                                                            <button key={t.key} type="button" className="qbc-pick" onClick={() => setLink(id, t.key)}>
                                                                {t.label}{t.code ? ` (${t.code})` : ''}
                                                            </button>
                                                        ))}
                                                        {pickerSearch.trim() && !pickerResults.length && <div className="qbc-muted">No partner, chain or hotel matches.</div>}
                                                        <button type="button" className="qbc-link-btn" onClick={() => { setPicking(null); setPickerSearch(''); }}>Cancel</button>
                                                    </div>
                                                ) : (
                                                    <button type="button" className="qbc-link-btn" onClick={() => { setPicking(id); setPickerSearch(''); }}>
                                                        {link ? 'Change' : 'Choose…'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!visible.length && <tr><td colSpan={7} className="qbc-empty">{error ? 'Customers could not be loaded.' : 'No customers match.'}</td></tr>}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {confirmModal && (
                <ConfirmModal
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmLabel={confirmModal.confirmLabel}
                    danger
                    onConfirm={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
}

export default QuickBooksCustomersPage;
